/* v8 ignore start -- @preserve */

import { tenantConfig } from "@tenant-config";

import { type ThemesType } from "./themesModel";

// Generate tenant-specific colors dynamically
export const getTenantColorsList = () => {
  const { branding } = tenantConfig;

  return {
    // Static colors that don't change per tenant
    black: "#0D1116",
    white: "#ffffff",
    "light-gray": "#E1E5F0",

    // Brand Colors (Light Theme) - Dynamic from tenant config
    "brand-default-light": branding.brandDefault.light,
    "brand-hover-light": branding.brandHover.light,
    "brand-active-light": branding.brandActive.light,
    "brand-opacity-light": branding.brandOpacity.light,
    "brand-default-10-light": branding.brandOpacity.light.replace("0.2", "0.1"),
    "brand-default-30-light": branding.brandOpacity.light.replace("0.2", "0.3"),
    "brand-default-50-light": branding.brandOpacity.light.replace("0.2", "0.5"),

    // Brand Colors (Dark Theme) - Dynamic from tenant config
    "brand-default-dark": branding.brandDefault.dark,
    "brand-hover-dark": branding.brandHover.dark,
    "brand-active-dark": branding.brandActive.dark,
    "brand-opacity-dark": branding.brandOpacity.dark,
    "brand-default-10-dark": branding.brandOpacity.dark.replace("0.2", "0.1"),
    "brand-default-30-dark": branding.brandOpacity.dark.replace("0.2", "0.3"),
    "brand-default-50-dark": branding.brandOpacity.dark.replace("0.2", "0.5"),

    // Neutral Colors (Light Theme) - Static
    "neutral-100-light": "#ffffff",
    "neutral-150-light": "#F9FBFB",
    "neutral-200-light": "#F1F3F4",
    "neutral-300-light": "#DEE1E5",
    "neutral-400-light": "#C6CBCE",
    "neutral-500-light": "#A9AEB0",
    "neutral-600-light": "#7B828A",
    "neutral-600-10-light": "rgba(123, 130, 138, 0.10)",
    "neutral-600-10-light-as-solid": "#f2f3f3",
    "neutral-600-50-light": "rgba(123,130,138, 0.5)",
    "neutral-650-light": "#6B7280",
    "neutral-700-light": "#585F67",
    "neutral-750-light": "#4b5563",
    "neutral-800-light": "#3C434B",
    "neutral-850-light": "#1F2429",
    "neutral-900-light": "#151A1F",

    // Neutral Colors (Dark Theme) - Static
    "neutral-100-dark": "#F3F4F9",
    "neutral-200-dark": "#C9D0E6",
    "neutral-300-dark": "#B3B8CC",
    "neutral-400-dark": "#737B89",
    "neutral-500-dark": "#525867",
    "neutral-600-dark": "#373D4F",
    "neutral-600-10-dark": "rgba(55, 61, 79, 0.1)",
    "neutral-600-50-dark": "rgba(55, 61, 79, 0.5)",
    "neutral-600-50-dark-as-solid": "#9b9ea7",
    "neutral-650-dark": "#525867",
    "neutral-700-dark": "#272A3C",
    "neutral-750-dark": "#1F1F24",
    "neutral-800-dark": "#1D2026",
    "neutral-850-dark": "#191B1F",
    "neutral-900-dark": "#0E0F10",

    // Support Colors (Light Theme) - Static
    "green-default-light": "#52A365",
    "orange-default-light": "#FFA14A",
    "red-default-light": "#DC1002",
    "blue-default-light": "#0666EB",

    // Support Colors (Dark Theme) - Static
    "green-default-dark": "#3ABB58",
    "orange-default-dark": "#FFA14A",
    "red-default-dark": "#D9443A",
    "blue-default-dark": "#1F78F3",

    // Link Colors (Light Theme) - Static
    "link-primary-default-light": "#0C66E4",
    "link-primary-hover-light": "#0C58C2",
    "link-primary-active-light": "#0648AB",

    // Link Colors (Dark Theme) - Static
    "link-primary-default-dark": "#0C66E4",
    "link-primary-hover-dark": "#0C58C2",
    "link-primary-active-dark": "#0648AB",

    // Additional Colors (Both Themes) - Static
    "gold-default": "#D0B162",
    "rose-default": "#B362D0",
    "violet-default": "#6462D0",

    // Random Colors (Both Themes) - Static
    "neutral-white-500": "#ABB1B5",
    "neutral-white-600": "#78808A",

    // Data Colors (Light Theme) - Dynamic from tenant config
    "data-1-light": branding.dataColors.light[0] || "#007B63",
    "data-2-light": branding.dataColors.light[1] || "#73A1D3",
    "data-3-light": branding.dataColors.light[2] || "#395878",
    "data-4-light": branding.dataColors.light[3] || "#93867B",
    "data-5-light": branding.dataColors.light[4] || "#F0AE7F",

    // Data Colors (Dark Theme) - Dynamic from tenant config
    "data-1-dark": branding.dataColors.dark[0] || "#008A6F",
    "data-2-dark": branding.dataColors.dark[1] || "#73A1D3",
    "data-3-dark": branding.dataColors.dark[2] || "#395878",
    "data-4-dark": branding.dataColors.dark[3] || "#93867B",
    "data-5-dark": branding.dataColors.dark[4] || "#F0AE7F",

    // Tracker Colors (Universal - not tenant-specific)
    "tracker-pending": "#FFA14A",
    "tracker-review": "#0666EB",
    "tracker-ready": "#93867B",
    "tracker-converted": "#007B63",
  } as const;
};

// Export tenant-aware themes
export const getTenantThemes = (): ThemesType => {
  const tenantColorsList = getTenantColorsList();

  // Use the same theme generation logic but with tenant colors
  const colorAlias = {
    dark: {
      background: {
        "brand/10%": tenantColorsList["brand-default-10-dark"],
        card: tenantColorsList["neutral-800-dark"],
        "card-header": tenantColorsList["neutral-850-dark"],
        component: tenantColorsList["neutral-600-50-dark"],
        componentFull: tenantColorsList["neutral-600-50-dark-as-solid"],
        primary: tenantColorsList["neutral-900-dark"],
        secondary: tenantColorsList["neutral-850-dark"],
        tertiary: tenantColorsList["neutral-800-dark"],
        subtle: tenantColorsList["neutral-850-dark"],
      },
      border: {
        primary: tenantColorsList["neutral-600-dark"],
        secondary: tenantColorsList["neutral-500-dark"],
        brand: tenantColorsList["brand-default-dark"],
        positive: tenantColorsList["green-default-dark"],
        warning: tenantColorsList["orange-default-dark"],
        pending: tenantColorsList["blue-default-dark"],
        negative: tenantColorsList["red-default-dark"],
      },
      interactive: {
        active: tenantColorsList["brand-active-dark"],
        default: tenantColorsList["brand-default-dark"],
        disabled: tenantColorsList["brand-opacity-dark"],
        hover: tenantColorsList["brand-hover-dark"],
      },
      typography: {
        primary: tenantColorsList["neutral-100-dark"],
        secondary: tenantColorsList["neutral-300-dark"],
        tertiary: tenantColorsList["neutral-400-dark"],
        disabled: tenantColorsList["neutral-500-dark"],
        positive: tenantColorsList["green-default-dark"],
        warning: tenantColorsList["orange-default-dark"],
        pending: tenantColorsList["blue-default-dark"],
        negative: tenantColorsList["red-default-dark"],
        interactive: tenantColorsList["brand-default-dark"],
      },
    },
    light: {
      background: {
        "brand/10%": tenantColorsList["brand-default-10-light"],
        card: tenantColorsList["neutral-100-light"],
        "card-header": tenantColorsList["neutral-150-light"],
        component: tenantColorsList["neutral-600-10-light"],
        componentFull: tenantColorsList["neutral-600-10-light-as-solid"],
        primary: tenantColorsList["neutral-100-light"],
        secondary: tenantColorsList["neutral-200-light"],
        tertiary: tenantColorsList["neutral-300-light"],
        subtle: tenantColorsList["neutral-150-light"],
      },
      border: {
        primary: tenantColorsList["neutral-300-light"],
        secondary: tenantColorsList["neutral-200-light"],
        brand: tenantColorsList["brand-default-light"],
        warning: tenantColorsList["orange-default-light"],
        pending: tenantColorsList["blue-default-light"],
        positive: tenantColorsList["green-default-light"],
        negative: tenantColorsList["red-default-light"],
      },
      interactive: {
        active: tenantColorsList["brand-active-light"],
        default: tenantColorsList["brand-default-light"],
        disabled: tenantColorsList["brand-opacity-light"],
        hover: tenantColorsList["brand-hover-light"],
      },
      typography: {
        primary: tenantColorsList["neutral-900-light"],
        secondary: tenantColorsList["neutral-600-light"],
        tertiary: tenantColorsList["neutral-500-light"],
        disabled: tenantColorsList["neutral-400-light"],
        positive: tenantColorsList["green-default-light"],
        warning: tenantColorsList["orange-default-light"],
        pending: tenantColorsList["blue-default-light"],
        negative: tenantColorsList["red-default-light"],
        interactive: tenantColorsList["brand-default-light"],
      },
    },
  } as const;

  const getTheme = (mode: "dark" | "light") => {
    const isLightMode = mode === "light";
    const colorMode = isLightMode ? colorAlias.light : colorAlias.dark;

    const buttonStyles = {
      destructive: {
        background: {
          default: isLightMode
            ? tenantColorsList["neutral-100-light"]
            : tenantColorsList["neutral-800-dark"],
          hover: isLightMode
            ? tenantColorsList["neutral-300-light"]
            : tenantColorsList["neutral-600-dark"],
          pressed: isLightMode
            ? tenantColorsList["neutral-400-light"]
            : tenantColorsList["neutral-500-dark"],
        },
        border: {
          default: isLightMode
            ? tenantColorsList["neutral-300-light"]
            : tenantColorsList["neutral-600-dark"],
        },
        foreground: {
          default: tenantColorsList[`red-default-${mode}`],
        },
      },
      primary: {
        background: {
          default: tenantColorsList[`brand-default-${mode}`],
          hover: tenantColorsList[`brand-hover-${mode}`],
          pressed: tenantColorsList[`brand-active-${mode}`],
        },
        foreground: {
          default: tenantColorsList[`neutral-100-${mode}`],
        },
      },
      secondary: {
        background: {
          default: isLightMode
            ? tenantColorsList["neutral-100-light"]
            : tenantColorsList["neutral-800-dark"],
          hover: isLightMode
            ? tenantColorsList["neutral-300-light"]
            : tenantColorsList["neutral-600-dark"],
          pressed: isLightMode
            ? tenantColorsList["neutral-400-light"]
            : tenantColorsList["neutral-500-dark"],
        },
        border: {
          default: isLightMode
            ? tenantColorsList["neutral-300-light"]
            : tenantColorsList["neutral-600-dark"],
        },
        foreground: {
          default: isLightMode
            ? tenantColorsList["neutral-900-light"]
            : tenantColorsList["neutral-100-dark"],
        },
      },
      tertiary: {
        background: {
          hover: colorMode.typography.primary,
          pressed: colorMode.typography.disabled,
        },
        foreground: {
          default: colorMode.typography.secondary,
        },
      },
      themed: {
        background: {
          default: tenantColorsList[`neutral-100-${mode}`],
        },
        border: {
          default: tenantColorsList[`neutral-300-${mode}`],
          hover: tenantColorsList["neutral-white-600"],
        },
      },
    };

    const iconButtonStyles = {
      background: {
        default: isLightMode
          ? tenantColorsList["neutral-100-light"]
          : tenantColorsList["neutral-800-light"],
        hover: isLightMode
          ? tenantColorsList["neutral-300-light"]
          : tenantColorsList["neutral-600-light"],
        pressed: isLightMode
          ? tenantColorsList["neutral-400-light"]
          : tenantColorsList["neutral-500-light"],
        border: isLightMode
          ? tenantColorsList["neutral-300-light"]
          : tenantColorsList["neutral-600-light"],
        foreground: isLightMode
          ? tenantColorsList["neutral-900-light"]
          : tenantColorsList["neutral-100-light"],
      },
      noBackground: {
        hover: isLightMode
          ? tenantColorsList["neutral-300-light"]
          : tenantColorsList["neutral-600-light"],
        pressed: isLightMode
          ? tenantColorsList["neutral-400-light"]
          : tenantColorsList["neutral-500-light"],
        default: isLightMode
          ? tenantColorsList["neutral-600-light"]
          : tenantColorsList["neutral-400-light"],
        foreground: isLightMode
          ? tenantColorsList["neutral-600-light"]
          : tenantColorsList["neutral-800-light"],
      },
    };

    const textLinkStyles = {
      primary: {
        default: tenantColorsList[`link-primary-default-${mode}`],
        hover: tenantColorsList[`link-primary-hover-${mode}`],
        active: tenantColorsList[`link-primary-active-${mode}`],
      },
      brand: {
        default: colorMode.interactive.default,
        hover: colorMode.interactive.hover,
        active: colorMode.interactive.active,
      },
    };

    const illustrationAlias = {
      foreground: {
        brand: tenantColorsList[`brand-default-${mode}`],
        primary: isLightMode
          ? tenantColorsList["neutral-100-light"]
          : tenantColorsList["neutral-400-dark"],
        secondary: isLightMode
          ? tenantColorsList["neutral-300-light"]
          : tenantColorsList["neutral-500-dark"],
      },
      background: {
        primary: isLightMode
          ? tenantColorsList["neutral-100-light"]
          : tenantColorsList["neutral-600-dark"],
        brand: tenantColorsList[`brand-opacity-${mode}`],
      },
    };

    const dataColors = {
      "data-1": tenantColorsList[`data-1-${mode}`],
      "data-2": tenantColorsList[`data-2-${mode}`],
      "data-3": tenantColorsList[`data-3-${mode}`],
      "data-4": tenantColorsList[`data-4-${mode}`],
      "data-5": tenantColorsList[`data-5-${mode}`],
    };

    return {
      // Brand - tenant-aware
      "brand-active": colorMode.interactive.active,
      "brand-hover": colorMode.interactive.hover,
      "brand-default": colorMode.interactive.default,
      "brand-disabled": colorMode.interactive.disabled,

      // Background
      "background/primary": colorMode.background.primary,
      "background/secondary": colorMode.background.secondary,
      "background/tertiary": colorMode.background.tertiary,
      "background/brand/opacity": colorMode.background["brand/10%"],
      "background/component": colorMode.background.component,
      "background/component-full": colorMode.background.componentFull,
      "background/card": colorMode.background.card,
      "background/card-header": colorMode.background["card-header"],
      "background/subtle": colorMode.background.subtle,

      // Border
      "border/primary": colorMode.border.primary,
      "border/secondary": colorMode.border.secondary,
      "border/warning": colorMode.border.warning,
      "border/brand": colorMode.border.brand,
      "border/pending": colorMode.border.pending,
      "border/positive": colorMode.border.positive,
      "border/negative": colorMode.border.negative,
      "border/action": colorMode.interactive.active,
      "border/action/active": colorMode.interactive.active,
      "border/action/hover": colorMode.interactive.hover,

      // Primary button - tenant-aware
      "button/primary/background/default":
        buttonStyles.primary.background.default,
      "button/primary/background/hover": buttonStyles.primary.background.hover,
      "button/primary/background/pressed":
        buttonStyles.primary.background.pressed,
      "button/primary/border/hover": buttonStyles.primary.background.hover,
      "button/primary/foreground/default":
        buttonStyles.primary.foreground.default,
      "button/primary/label/default": buttonStyles.primary.foreground.default,

      // Destructive button
      "button/destructive/background/default":
        buttonStyles.destructive.background.default,
      "button/destructive/background/hover":
        buttonStyles.destructive.background.hover,
      "button/destructive/background/pressed":
        buttonStyles.destructive.background.pressed,
      "button/destructive/border/default":
        buttonStyles.destructive.border.default,
      "button/destructive/border/hover":
        buttonStyles.destructive.background.hover,
      "button/destructive/border/pressed":
        buttonStyles.destructive.background.pressed,
      "button/destructive/foreground/default":
        buttonStyles.destructive.foreground.default,
      "button/destructive/label/default":
        buttonStyles.destructive.foreground.default,

      // Secondary button
      "button/secondary/background/default":
        buttonStyles.secondary.background.default,
      "button/secondary/background/hover":
        buttonStyles.secondary.background.hover,
      "button/secondary/background/pressed":
        buttonStyles.secondary.background.pressed,
      "button/secondary/border/default": buttonStyles.secondary.border.default,
      "button/secondary/border/hover": buttonStyles.secondary.background.hover,
      "button/secondary/border/pressed":
        buttonStyles.secondary.background.pressed,
      "button/secondary/foreground/default":
        buttonStyles.secondary.foreground.default,
      "button/secondary/label/default":
        buttonStyles.secondary.foreground.default,

      // Tertiary button
      "button/tertiary/foreground/default":
        buttonStyles.tertiary.foreground.default,
      "button/tertiary/foreground/hover":
        buttonStyles.tertiary.background.hover,
      "button/tertiary/foreground/pressed":
        buttonStyles.tertiary.background.pressed,

      // Themed button - tenant-aware
      "button/themed/background/default":
        buttonStyles.themed.background.default,
      "button/themed/border/default": buttonStyles.themed.border.default,
      "button/themed/border/hover": buttonStyles.themed.border.hover,

      // Icon Button/background
      "icon-button/background/default": iconButtonStyles.background.default,
      "icon-button/background/hover": iconButtonStyles.background.hover,
      "icon-button/background/pressed": iconButtonStyles.background.pressed,
      "icon-button/background/border": iconButtonStyles.background.border,
      "icon-button/background/foreground":
        iconButtonStyles.background.foreground,

      // Icon Button/no-background
      "icon-button/no-background/default":
        iconButtonStyles.noBackground.default,
      "icon-button/no-background/hover": iconButtonStyles.noBackground.hover,
      "icon-button/no-background/pressed":
        iconButtonStyles.noBackground.pressed,
      "icon-button/no-background/foreground":
        iconButtonStyles.noBackground.foreground,

      // Misc
      "dark-gray": isLightMode
        ? tenantColorsList["neutral-100-light"]
        : tenantColorsList["neutral-900-dark"],
      "progress-bar/background/secondary": isLightMode
        ? tenantColorsList["neutral-200-light"]
        : tenantColorsList.black,
      "svg/fill/circle/primary": isLightMode
        ? tenantColorsList["neutral-100-light"]
        : tenantColorsList.black,

      // Table
      "table/background/secondary": tenantColorsList[`neutral-300-${mode}`],
      "table/body/label/secondary": tenantColorsList[`neutral-600-${mode}`],
      "table/body/text/primary": isLightMode
        ? tenantColorsList["neutral-850-light"]
        : tenantColorsList["neutral-100-dark"],
      "table/body/text/primary/hover": isLightMode
        ? tenantColorsList["neutral-100-light"]
        : tenantColorsList["neutral-800-dark"],
      "table/head/label/secondary": tenantColorsList["neutral-600-dark"],
      "table/label/secondary": tenantColorsList["neutral-white-500"],
      "table/label/themed": isLightMode
        ? tenantColorsList["neutral-700-light"]
        : tenantColorsList["neutral-300-dark"],

      // Text
      "text/action": colorMode.interactive.active,
      "text/interactive": colorMode.interactive.default,
      "text/action/active": colorMode.interactive.active,
      "text/action/hover": colorMode.interactive.hover,
      "text/disabled": colorMode.typography.disabled,
      "text/primary": colorMode.typography.primary,
      "text/pending": colorMode.typography.pending,
      "text/negative": colorMode.typography.negative,
      "text/positive": colorMode.typography.positive,
      "text/secondary": colorMode.typography.secondary,
      "text/tertiary": colorMode.typography.tertiary,
      "text/themed": isLightMode
        ? tenantColorsList["neutral-white-500"]
        : tenantColorsList["neutral-700-dark"],
      "text/warning": colorMode.typography.warning,
      "text/on-brand": tenantColorsList[`neutral-100-${mode}`],

      // Text / Link / Primary
      "text/link/primary/default": textLinkStyles.primary.default,
      "text/link/primary/hover": textLinkStyles.primary.hover,
      "text/link/primary/active": textLinkStyles.primary.active,

      // Text / Link / Brand
      "text/link/brand/default": textLinkStyles.brand.default,
      "text/link/brand/hover": textLinkStyles.brand.hover,
      "text/link/brand/active": textLinkStyles.brand.active,

      // Illustrations
      "illustration-background-brand": illustrationAlias.background.brand,
      "illustration-background-primary": illustrationAlias.background.primary,
      "illustration-foreground-brand": illustrationAlias.foreground.brand,
      "illustration-foreground-primary": illustrationAlias.foreground.primary,
      "illustration-foreground-secondary":
        illustrationAlias.foreground.secondary,

      // Data colors - tenant-aware
      "data-1": dataColors["data-1"],
      "data-2": dataColors["data-2"],
      "data-3": dataColors["data-3"],
      "data-4": dataColors["data-4"],
      "data-5": dataColors["data-5"],

      // Tracker colors - universal
      "tracker-pending": tenantColorsList["tracker-pending"],
      "tracker-review": tenantColorsList["tracker-review"],
      "tracker-ready": tenantColorsList["tracker-ready"],
      "tracker-converted": tenantColorsList["tracker-converted"],
    } as const;
  };

  const lightTheme = getTheme("light");
  const darkTheme = getTheme("dark");

  return {
    dark: darkTheme,
    default: lightTheme,
    light: lightTheme,
  } satisfies ThemesType;
};
